import React, { useState, useEffect, useContext, useMemo } from "react";
import { useAuth } from "../../../AuthContext";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "api/axiosInstance";
import { trackEvent } from "utils/analytics";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import SignInLayout from "layouts/authentication/components/SignInLayout";
import AuthPromoPanel from "layouts/authentication/components/AuthPromoPanel";
import WelcomeModal from "layouts/authentication/components/WelcomeModal";
import CircularProgress from "@mui/material/CircularProgress";
import { LanguageContext } from "../../../context/LanguageContext";
import { normalizeLang } from "../../language/langUtil";
import { saveReferralCodeFromUrl, getReferralCode, getAuthRedirectPath } from "../../../utils/referral";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Stack,
  Button,
  MobileStepper,
  Checkbox,
  FormControlLabel,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  Icon,
} from '@mui/material';

import "./index.css";
import { useGoogleLogin } from '@react-oauth/google';
import { COLORS, TYPO, RADIUS } from "theme/tokens";

export default function SignIn() {
  const [status, setStatus] = useState({ loading: false, message: "", error: false });
  const [formData, setFormData] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const [googleEmail, setGoogleEmail] = useState(null);
  const [user, setuser] = useState(null);
  const [openWelcome, setOpenWelcome] = useState(false);
  const [accessToken, setaccessToken] = useState(null);
  const [refreshToken, setrefreshToken] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState("");
  useEffect(() => {
    // 頁面一進來就嘗試存推薦碼
    saveReferralCodeFromUrl();

    // 如果 localStorage 已有推薦碼，預先放到 state
    const savedRef = getReferralCode();
    if (savedRef) {
      setReferralCodeInput(savedRef);
    }
  }, []);
  const { componentLanguageData, loadLanguageFile, language, changeLanguage } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("signin", "authentication/sign-in/sign-in_lang");
  }, [language, loadLanguageFile]);
  const LANGUAGE = useMemo(() => {
    return componentLanguageData.signin || { Main: {}, Error: {}, Alert: {}, Slides: {}, Dialog: {}, Fistlogin: {} };
  }, [componentLanguageData]);

  // ── Google 登入處理（access_token flow，完全繞過 FedCM / iframe / 403 問題）──
  const handleGoogleSuccess = async (data) => {
    try {
      setStatus({ loading: true, message: "", error: false });
      const res = await axiosInstance.post("/auth/google-login", data);
      if (res.data.result && res.data.accessToken) {
        setGoogleEmail(res.data.email);
        setaccessToken(res.data.accessToken);
        setrefreshToken(res.data.refreshToken);
        setuser(res.data.user);
        if (res.data.hasPassword) {
          login(res.data.accessToken, res.data.user, "google");
          localStorage.removeItem("referralCode");
          navigate(getAuthRedirectPath());
        } else {
          setShowPasswordModal(true);
        }
      } else {
        toast.error(LANGUAGE.Alert.Content3);
      }
    } catch (error) {
      // 2026-06-08 顯示後端真正錯誤（如「Google 驗證逾時…」），方便辨識而非籠統訊息
      const backendMsg = error?.response?.data?.error;
      console.error(LANGUAGE.Alert.Content4, error);
      // 修補 H5：封鎖帳號 Google 登入 → 顯示明確停權訊息（後端回 Error_login_banned）
      if (backendMsg === "Error_login_banned") {
        toast.error(LANGUAGE.Error.Content12);
      } else {
        toast.error(backendMsg || LANGUAGE.Alert.Content4);
      }
    } finally {
      setStatus({ loading: false, message: "", error: false });
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: (tokenResponse) => handleGoogleSuccess({ accessToken: tokenResponse.access_token }),
    onError: () => toast.error(LANGUAGE.Alert.Content4),
  });

  // 灰底填滿、無外框的 input 樣式（對齊 design guideline）
  const filledInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.grayLightest,
      borderRadius: 1.5,
      minHeight: 44,
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused": {
        backgroundColor: COLORS.white,
        "& fieldset": { border: "1px solid", borderColor: "info.main" },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 12px",
      ...TYPO.bodyM,
      color: COLORS.black,
      "&::placeholder": {
        ...TYPO.bodyM,
        color: COLORS.grayMedian, // #C4C4C4
        opacity: 1,
      },
    },
  };

  // console.log("目前語言代碼:", language);

  

  const [isReferralLocked, setIsReferralLocked] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showModalPwd, setShowModalPwd] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    label: LANGUAGE.Main.Content11,
    percent: 0,
    color: "error",
  });
  const [passwordError, setPasswordError] = useState("");
  const checkPasswordStrength = (pwd) => {
  let score = 0;
  const rules = [
      /[a-z]/.test(pwd),
      /[A-Z]/.test(pwd),
      /\d/.test(pwd),
      /[\W_]/.test(pwd),
      pwd.length >= 8,
    ];
    score = rules.filter(Boolean).length;

    if (score <= 2)
      return { label: LANGUAGE.Main.Content11, percent: 25, color: "error" };
    else if (score === 3)
      return { label: LANGUAGE.Main.Content12, percent: 50, color: "warning" };
    else if (score === 4)
      return { label: LANGUAGE.Main.Content13, percent: 75, color: "info" };
    else
      return { label: LANGUAGE.Main.Content14, percent: 100, color: "success" };
  };

  const renderStrengthLabel = (strength) => strength.label;
    
  const isPasswordValid = () => {
    return (
      password &&
      confirmPassword &&
      password === confirmPassword &&
      passwordStrength.percent >= 75
    );
  };



  const handlePasswordSubmit = async () => {
    if (!isPasswordValid()) {
      setPasswordError(LANGUAGE.Error.Content9);
      return;
    }

    try {
      // 🛡️ C1：帶上 Google 登入已核發的 accessToken（此時尚未寫入 localStorage，interceptor 不會覆蓋顯式 header）；
      //   email 不再由前端指定，後端一律以 session（req.user）為準，杜絕越權改他人密碼。
      const res = await axiosInstance.post(
        "/auth/google_set_password",
        { password, referralCodeInput },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setShowPasswordModal(false);
      // ✅ 呼叫你的 useAuth 方法（假設 login 是 context 方法）
      const firstLogin = res.data.firstLogin;
      localStorage.setItem("firstLogin", firstLogin);
      if (firstLogin === true) {
        // GA4：Google 新帳號完成設定 = 首次註冊（帳密註冊走 sign-up 頁的 submitRegister）
        trackEvent("sign_up", { method: "google" });
        setOpenWelcome(true);
      }else{
        login(accessToken, user, "google");
        toast.success(LANGUAGE.Alert.Content5);
        navigate(getAuthRedirectPath());
      }
      
    } catch (err) {
      console.error(err);
      let message_code = "";
      if (err.response?.data?.message === "Error_register_004") message_code = LANGUAGE.Error.Content11;
      toast.error(message_code || LANGUAGE.Error.Content10);
    }
  };


  // 處理輸入變更
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const { login } = useAuth();

  // 提交登入請求
  const handleLogin = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, message: "", error: false }); // 開始 loading
    try {
      localStorage.setItem("pendingVerifyEmail", formData.email);
    } catch (err) {
      console.warn("⚠️ 無法儲存 pendingVerifyEmail：", err);
    }
    try {
      const response = await axiosInstance.post("/auth/login", formData);
      // 修補 C3b/H6：存第一階段 pending token；verify-token 需帶它(證明已過密碼)才會發正式 session
      localStorage.setItem("pendingToken", response.data?.pendingToken || "");
      navigate("/authentication/2fa");
    } catch (error) {
      if (error.response?.data?.message === "Error_login_001") {
        toast.error(LANGUAGE.Error.Content2);
      } else if (error.response?.data?.message === "Error_login_003") {
        toast.error(LANGUAGE.Error.Content4);
      } else if (error.response?.data?.message === "Error_login_004") {
        toast.error(LANGUAGE.Error.Content5);
      } else if (error.response?.data?.message === "Error_login_banned") {
        toast.error(LANGUAGE.Error.Content12);
      } else if (error.response?.data?.message === "Error_login_locked") {
        // M11：帳號層級鎖定（連續登入失敗達上限）
        toast.error(LANGUAGE.Error.Content13);
      } else if (error.response?.data?.message === "Error_rateLimit_003") {
        toast.error(LANGUAGE.Error.Content6);
      } else if (error.response?.data?.message === "Error_login_002") {
        toast.error(LANGUAGE.Error.Content3);
        navigate("/authentication/verify-email");
        return;
      } else {
        toast.error(LANGUAGE.Error.Content1);
      }

      setStatus({ loading: false, message: "", error: false }); // 僅重置 loading
    }
  };

  useEffect(() => {
    if (status.loading) {
      document.body.classList.add("loading");
    } else {
      document.body.classList.remove("loading");
    }
    return () => {
      document.body.classList.remove("loading");
    };
  }, [status.loading]);


  // 2026-06-17：依 Sean 要求，登入頁永久不再彈出「登入後能做什麼」教學輪播（直接移除觸發，
  //   不再讀寫 signinIntroDismissed_v1）。本 effect 只保留 URL ?lang= 語系同步（官網導流用）。
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // 官網導流帶 ?lang=（短碼 tw/cn/en/uk 或 canonical 皆可）→ 正規化後同步全站。
    //   與 ?redirect= / ?price=（登入後導頁）是不同參數、互不影響，可同網址並存。
    const fromUrl = normalizeLang(params.get("lang"));
    if (fromUrl && fromUrl !== language) {
      changeLanguage(fromUrl);
    }
  }, [language, changeLanguage]);

  useEffect(() => {
  const savedRef = getReferralCode(); // 從 localStorage 取推薦碼
  if (savedRef) {
      // 自動帶入並鎖定
      setFormData((prev) => ({ ...prev, referralCodeInput: savedRef }));
      setIsReferralLocked(true);
    }
  }, []);


  return (

    
    <SignInLayout>
      <Card
        sx={{
          width: "100%",
          maxWidth: 1000,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Grid container>
          {/* === 左欄：促銷面板（15 天試用 + AI 流程 + 三大特點）=== */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: "none", md: "block" },
              borderRight: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <AuthPromoPanel />
          </Grid>

          {/* === 右欄：表單 === */}
          <Grid item xs={12} md={6}>
            {/* 間距改用百分比：隨面板寬度按比例縮放（取代固定 px） */}
            <MDBox px={{ xs: 3, sm: 5, md: "12%" }} py={{ xs: 4, md: 6 }}>
              {/* 手機版才顯示的 logo */}
              <MDBox
                display={{ xs: "flex", md: "none" }}
                justifyContent="center"
                mb={3}
              >
                <img
                  src={
                    language === "zh-TW" || language === "zh-CN"
                      ? "/images/logo/logo_2_1.png"
                      : "/images/logo/logo_2_2.png"
                  }
                  alt="MASQuant"
                  style={{ maxHeight: 120, objectFit: "contain" }}
                />
              </MDBox>

              <MDTypography sx={{ ...TYPO.displayM, color: COLORS.black, textAlign: "center" }} mb={0.5}>
                {LANGUAGE.Main.Content32}
              </MDTypography>
              <MDTypography sx={{ ...TYPO.title, color: COLORS.grayDark, textAlign: "center" }} mb={3}>
                {LANGUAGE.Main.Content33}
              </MDTypography>

              {/* Google 登入：大按鈕置頂（access_token flow，繞過 FedCM/iframe）*/}
              <MDBox
                onClick={() => !status.loading && googleLogin()}
                sx={{
                  height: 52,
                  borderRadius: "12px",
                  border: `1px solid ${COLORS.grayLighter}`,
                  background: COLORS.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  cursor: status.loading ? "not-allowed" : "pointer",
                  transition: "border-color 0.15s, background 0.15s",
                  "&:hover": { borderColor: COLORS.main, background: COLORS.grayLightest },
                }}
              >
                <img src="/images/Google.svg" alt="" style={{ width: 22, height: 22 }} />
                <span style={{ ...TYPO.title, color: COLORS.black }}>{LANGUAGE.Main.Content29}</span>
              </MDBox>
              <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.grayDark, textAlign: "center", mt: 1 }}>
                {LANGUAGE.Main.Content30}
              </MDTypography>

              {/* 或使用 Email 分隔線 */}
              <MDBox display="flex" alignItems="center" my={3}>
                <Divider sx={{ flex: 1 }} />
                <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.black, px: 1.5, whiteSpace: "nowrap" }}>
                  {LANGUAGE.Main.Content31}
                </MDTypography>
                <Divider sx={{ flex: 1 }} />
              </MDBox>

              <MDBox component="form" onSubmit={handleLogin}>
                {/* Email */}
                <MDBox mb={2}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, display: "block", mb: 0.75, color: COLORS.black }}
                  >
                    Email<span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="email"
                    placeholder={LANGUAGE.Main.Content27}
                    fullWidth
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    sx={filledInputSx}
                  />
                </MDBox>

                {/* Password */}
                <MDBox mb={2.5}>
                  <MDTypography
                    sx={{ ...TYPO.bodyS, display: "block", mb: 0.75, color: COLORS.black }}
                  >
                    {LANGUAGE.Main.Content2}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type={showPassword ? "text" : "password"}
                    placeholder={LANGUAGE.Main.Content28}
                    fullWidth
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    sx={filledInputSx}
                    InputProps={
                      formData.password.length > 0
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword((v) => !v)}
                                  edge="end"
                                  size="small"
                                  aria-label="toggle password visibility"
                                  tabIndex={-1}
                                >
                                  <Icon fontSize="small">
                                    {showPassword ? "visibility" : "visibility_off"}
                                  </Icon>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                  {/* 2026-05-25 忘記密碼移到密碼欄位下方、右對齊、橘色 + 底線（依 Sean 截圖二）
                      className auth-underline 才能覆寫全站 a{text-decoration:none !important} */}
                  <MDBox display="flex" justifyContent="flex-end" mt={0.75}>
                    <MDBox
                      component="a"
                      href="/authentication/forget-password"
                      className="auth-underline"
                      sx={{
                        color: COLORS.main,
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: 1.6,
                        cursor: "pointer",
                        transition: "color 0.15s ease",
                        "&:hover": { color: COLORS.main },
                      }}
                    >
                      {`${LANGUAGE.Main.Content5}?`}
                    </MDBox>
                  </MDBox>
                </MDBox>

                {/* 登入按鈕 */}
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  size="large"
                  disabled={status.loading}
                  sx={{
                    ...TYPO.title,
                    color: COLORS.white,
                    textTransform: "none",
                    borderRadius: 2,
                    background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
                    },
                    "&:disabled": {
                      background: "linear-gradient(90deg, #f88e2b 0%, #ffd166 100%)",
                      opacity: 0.6,
                    },
                  }}
                >
                  {status.loading ? LANGUAGE.Alert.Content1 : LANGUAGE.Alert.Content2}
                </MDButton>

                {/* 註冊文字連結（兩行） */}
                <MDBox
                  mt={3}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap={0.5}
                >
                  <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.black }}>
                    {LANGUAGE.Main.Content26}
                  </MDTypography>
                  <Link
                    to="/authentication/sign-up"
                    className="auth-underline"
                    style={{
                      ...TYPO.title,
                      color: COLORS.main,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <img
                      src="/images/icon/Vector-Right.png"
                      alt=""
                      style={{ width: 16, height: 16, objectFit: "contain" }}
                    />
                    {LANGUAGE.Main.Content4}
                  </Link>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
      {status.loading && (
        <div className="loading-overlay">
          <CircularProgress color="inherit" />
        </div>
      )}

      {/* === 設定交易密碼：改成跟註冊/登入同款雙欄卡片（依設計稿）=== */}
      <Dialog
        open={showPasswordModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", width: "100%", maxWidth: 920 } }}
      >
        <Grid container sx={{ minHeight: { md: 560 } }}>
          {/* 左欄：品牌 logo（白底 + 右框線）*/}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: COLORS.white,
              borderRight: `1px solid ${COLORS.grayLighter}`,
              px: 8,
              py: 6,
            }}
          >
            <MDBox
              component="img"
              src={(language === "zh-TW" || language === "zh-CN") ? "/images/logo/logo_2_1.png" : "/images/logo/logo_2_2.png"}
              alt="MASQuant"
              sx={{ width: 162, height: 254, objectFit: "contain" }}
            />
          </Grid>

          {/* 右欄：表單（白底；內容太長 → 框內 scroll）*/}
          <Grid item xs={12} md={7} sx={{ display: "flex", justifyContent: "center", background: COLORS.white }}>
            <MDBox
              sx={{
                width: "100%",
                maxWidth: 510,
                maxHeight: { md: 560 },
                overflowY: "auto",
                px: { xs: 3, sm: 4, md: "19px" },
                py: { xs: 3, md: 5 },
              }}
            >
              {/* 請設定安全交易密碼 — displayM/#000/500 */}
              <MDTypography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 500, mb: 1 }}>
                {LANGUAGE.Dialog.Title}
              </MDTypography>
              {/* 副標 — title/#8A8A8A/500 */}
              <MDTypography sx={{ ...TYPO.title, color: COLORS.grayDark, fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                {LANGUAGE.Dialog.Subtitle}
              </MDTypography>

              {/* 密碼* */}
              <MDBox mb={2}>
                <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}>
                  {LANGUAGE.Main.Content2}<span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                </MDTypography>
                <MDInput
                  type={showModalPwd ? "text" : "password"}
                  placeholder={LANGUAGE.Main.Content28}
                  fullWidth
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordStrength(checkPasswordStrength(e.target.value));
                  }}
                  sx={filledInputSx}
                  error={!!passwordError}
                  InputProps={
                    password.length > 0
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowModalPwd((v) => !v)} edge="end" size="small" aria-label="toggle password visibility" tabIndex={-1}>
                                <Icon fontSize="small">{showModalPwd ? "visibility" : "visibility_off"}</Icon>
                              </IconButton>
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
                {/* 強度條（有輸入才顯示）*/}
                {password.length > 0 && (
                  <MDBox mt={1}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <MDTypography variant="caption" sx={{ color: COLORS.grayDark }}>
                        {LANGUAGE.Main.Content8}
                      </MDTypography>
                      <MDTypography variant="caption" color={passwordStrength.color} fontWeight="medium">
                        {renderStrengthLabel(passwordStrength)}
                      </MDTypography>
                    </MDBox>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength.percent}
                      color={passwordStrength.color}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </MDBox>
                )}
              </MDBox>

              {/* 確認密碼* */}
              <MDBox mb={2}>
                <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}>
                  {LANGUAGE.Dialog.LabelConfirm}<span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                </MDTypography>
                <MDInput
                  type={showModalConfirm ? "text" : "password"}
                  placeholder={LANGUAGE.Main.Content9}
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={filledInputSx}
                  error={confirmPassword.length > 0 && password !== confirmPassword}
                  InputProps={
                    confirmPassword.length > 0
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowModalConfirm((v) => !v)} edge="end" size="small" aria-label="toggle password visibility" tabIndex={-1}>
                                <Icon fontSize="small">{showModalConfirm ? "visibility" : "visibility_off"}</Icon>
                              </IconButton>
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
                {confirmPassword.length > 0 && password !== confirmPassword && (
                  <MDTypography variant="caption" sx={{ color: COLORS.error, mt: 0.5, display: "block" }}>
                    {LANGUAGE.Error.Content7}
                  </MDTypography>
                )}
              </MDBox>

              {/* 推薦碼 */}
              <MDBox mb={3}>
                <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 0.75 }}>
                  {isReferralLocked ? LANGUAGE.Main.Content20 : LANGUAGE.Main.Content19}
                </MDTypography>
                <MDInput
                  type="text"
                  placeholder={LANGUAGE.Main.Content22}
                  fullWidth
                  value={referralCodeInput}
                  onChange={(e) => setReferralCodeInput(e.target.value)}
                  sx={filledInputSx}
                  InputProps={{ readOnly: isReferralLocked }}
                />
              </MDBox>

              {/* 確認送出 */}
              <MDButton
                onClick={handlePasswordSubmit}
                variant="gradient"
                color="info"
                fullWidth
                disabled={!isPasswordValid()}
                sx={{
                  height: 60,
                  borderRadius: "16px",
                  background: COLORS.gradient,
                  boxShadow: "4px 4px 9px 0 rgba(222, 86, 15, 0.20)",
                  textTransform: "none",
                  ...TYPO.title,
                  color: `${COLORS.white} !important`,
                  fontWeight: 500,
                  "&:hover": {
                    background: COLORS.gradient,
                    boxShadow: "4px 4px 12px 0 rgba(222, 86, 15, 0.30)",
                  },
                  "&:disabled": {
                    background: COLORS.gradient,
                    color: `${COLORS.white} !important`,
                    opacity: 0.5,
                  },
                }}
              >
                {LANGUAGE.Dialog.SubmitBtn}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Dialog>
      {/* 2026-06-08 首登歡迎 modal 改用共用元件 WelcomeModal（截圖二版型）*/}
      <WelcomeModal
        open={openWelcome}
        onClose={() => {
          setOpenWelcome(false);
          login(accessToken, user, "google");
          navigate(getAuthRedirectPath());
        }}
        onExperience={() => {
          setOpenWelcome(false);
          login(accessToken, user, "google");
          // 首登「立即體驗」導到 MAS 精靈（許願頁）
          navigate("/mas-genie?force=true");
        }}
        onLearnMore={() => {
          setOpenWelcome(false);
          login(accessToken, user, "google");
          navigate("/plans");
        }}
      />
    </SignInLayout>

  );
}


